import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { connect } from "react-redux";
import { CSVLink } from "react-csv";
import axios from "axios";
import { format } from "date-fns";
import {
  getReferrals,
  searchReferrals,
} from "../../store/actions/referralActions";
import { Table, Spin, Button, Input, message } from "antd";
import { SmileOutlined } from "@ant-design/icons";
import { addDays, subDays } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

const { Search } = Input;

const columns = [
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sortDirections: ["ascend", "descend"],
    sorter: (a, b) => a.status.localeCompare(b.status),
  },
  {
    title: "DataCompra",
    dataIndex: "paid_at",
    key: "paid_at",
    sorter: (a, b) => new Date(a.paid_at) - new Date(b.paid_at),
    render: (paid_at) => format(new Date(paid_at), "dd/MM/yyyy HH:mm:ss"),
  },
  {
    title: "MeioPagamento",
    dataIndex: "variables",
    key: "variables",
    sortDirections: ["ascend", "descend"],
    sorter: (a, b) => {
      let paymentMethodA =
        a.variables.find((item) => item.variable === "payment_method")?.value ||
        "";
      let paymentMethodB =
        b.variables.find((item) => item.variable === "payment_method")?.value ||
        "";
      return paymentMethodA.localeCompare(paymentMethodB);
    },
    render: (variables) =>
      variables.find((item) => item.variable === "payment_method")?.value,
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
    sortDirections: ["ascend", "descend"],
    sorter: (a, b) => a.email.localeCompare(b.email),
  },
  {
    title: "ValorPago",
    dataIndex: "total",
    key: "total",
    sorter: (a, b) => Number(a.total) - Number(b.total),
  },

  {
    title: "Estado",
    dataIndex: "payer_address_state",
    key: "payer_address_state",
    sortDirections: ["ascend", "descend"],
    sorter: (a, b) =>
      a.payer_address_state.localeCompare(b.payer_address_state),
  },
  {
    title: "Descrição",
    dataIndex: "items",
    key: "items",
    render: (items) => (
      <span
        dangerouslySetInnerHTML={{
          __html: items.map((item) => item.description).join("<br />"),
        }}
      />
    ),
  },
];

const ReferralList = ({
  referrals,
  getReferrals,
  searchReferrals,
  searchResult,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingCSV, setLoadingCSV] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedResults, setSearchedResults] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [allowedRange, setAllowedRange] = useState(null);
  const [csvData, setCsvData] = useState("");
  const [csvLink, setCsvLink] = useState(null);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      await getReferrals();
      setLoading(false);
    };
    getData();
  }, [getReferrals]);

  useEffect(() => {
    setSearchedResults(searchResult || []);
  }, [searchResult]);

  const handleDateChange = async (dates) => {
    setLoading(true);

    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);

    if (!start && !end) {
      setSearchedResults([]);
      setLoading(false);
      return;
    }

    if (start) {
      setAllowedRange({
        start: subDays(start, 30),
        end: addDays(start, 30),
      });
    } else {
      setAllowedRange(null);
    }

    const formattedStart = start ? format(start, "yyyy-MM-dd") : null;
    const formattedEnd = end ? format(end, "yyyy-MM-dd") : null;

    if (formattedStart && formattedEnd) {
      await searchReferrals(searchText, [formattedStart, formattedEnd]);
    }

    setLoading(false);
  };

  const onSearch = async (value) => {
    if (value) {
      await searchReferrals(value);
    } else {
      setSearchedResults([]);
    }
  };

  const handleSearchChange = (e) => {
    if (e.target.value === "") {
      setSearchedResults([]);
    }
    setSearchText(e.target.value);
  };

  const handleCSV = async () => {
    try {
      setLoadingCSV(true);

      const formattedStart = startDate ? format(startDate, "yyyy-MM-dd") : null;
      const formattedEnd = endDate ? format(endDate, "yyyy-MM-dd") : null;
      const response = await axios.get(
        `${process.env.REACT_APP_GOLANG_API}/api/v2/referral/users-purchases?paid_at_to=${formattedEnd}&paid_at_from=${formattedStart}`,
      );

      if (response) {
        const csvText = await response.data;
        setCsvData(csvText);
        csvLink.link.click();

        message.success("CSV exportado com sucesso!");
      } else {
        message.error("Erro ao buscar CSV.");
      }

      setLoadingCSV(false);
    } catch (error) {
      setLoadingCSV(false);
      message.error("Ocorreu um erro ao tentar exportar o CSV.");
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex" }}>
          <div>
            <CSVLink
              data={csvData}
              filename="referral.csv"
              className="hidden"
              ref={(link) => setCsvLink(link)}
            />

            <Button
              type="primary"
              size="middle"
              onClick={handleCSV}
              loading={loadingCSV}
            >
              {loadingCSV ? (
                <>Loading</>
              ) : (
                <>
                  <SmileOutlined /> Exportar
                </>
              )}
            </Button>
          </div>

          <Search
            size="middle"
            value={searchText}
            placeholder="Search here.."
            onSearch={onSearch}
            style={{ width: 200 }}
            onChange={handleSearchChange}
          />
        </div>
        <div style={{ display: "flex" }}>
          <DatePicker
            selected={startDate}
            onChange={handleDateChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            includeDateIntervals={allowedRange ? [allowedRange] : undefined}
            className="custom-datepicker"
            placeholderText="Selecione as datas"
            dateFormat="dd/MM/yyyy"
            isClearable
          />
        </div>
      </div>
      <Spin spinning={loading}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={
            (searchText || startDate || endDate) && searchedResults
              ? searchedResults
              : Array.isArray(referrals)
                ? referrals
                : []
          }
        />
      </Spin>
    </div>
  );
};

const mapStateToProps = (state) => ({
  referrals: state.referrals.referrals,
  searchResult: state.referrals.searchResult,
  searchError: state.referrals.searchError,
});

export default connect(mapStateToProps, {
  getReferrals,
  searchReferrals,
})(ReferralList);
